import { Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import { ApplicationService, NomineeTypeInfo, SchoolGroupInfo, SchoolInfo, WindowTypeInfo } from 'services/application/application.service';
import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { DownloadService, HttpMethod } from 'services/download/download.service';
import { ErrorService } from 'services/error/error.service';
import { ReportsService, ReportInfo } from 'services/reports/reports.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService, UserInfo } from 'services/user/user.service';
import { SettingsService } from 'services/settings/settings.service';

@Component({
    selector: 'app-reports',
    styleUrls: ['reports.component.scss'],
    templateUrl: 'reports.component.html'
})

export class ReportsComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    AllSchoolYears: string[];
    AvailableNomineeTypes: NomineeTypeInfo[];
    AvailableReports: ReportInfo[];
    AvailableSchoolGroups: SchoolGroupInfo[];
    AvailableSchools: SchoolInfo[];
    AvailableWindowTypes: WindowTypeInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchoolYear: string;
    User: UserInfo;
    ViewReady: boolean;

    constructor(private appSvc: ApplicationService, private awardSvc: AwardsService, private dialogSvc: DialogService,
		private downloadSvc: DownloadService, private errorSvc: ErrorService, private reportsSvc: ReportsService,
		private splashSvc: SplashService, private userSvc: UserService, private settingsSvc: SettingsService) {

    }

    ngOnDestroy() {
        this._subrelease.next(); this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AllSchoolYears = null;
        self.AvailableNomineeTypes = null;
        self.AvailableReports = null;
        self.AvailableSchoolGroups = null;
        self.AvailableSchools = null;
        self.AvailableWindowTypes = null;
        self.AwardInfo = null;
        self.CurrentSchoolYear = null;
        self.User = null;
        self.ViewReady = false;

        // Wait for the first non-null user object, to get authtoken info
        self.userSvc.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_user => _user && !!_user.AuthToken)
        ).subscribe(_user => {
            self.User = _user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userSvc.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_u => { self.User = _u; });

			self.settingsSvc.CurrentSchoolYearSubject.pipe(takeUntil(self._subrelease)).subscribe(year => {
				self.CurrentSchoolYear = year;

				// Load static data for use in the compoment.
				const availSchoolsObs = self.appSvc.GetAllSchools(self.CurrentSchoolYear);
				const curAwardObs = self.awardSvc.CurrentAwardsSub;
				const awdWindowTypeObs = self.appSvc.GetWindowTypes();
				const awdNomineeTypeObs = self.appSvc.GetNomineeTypes();

				combineLatest(availSchoolsObs, curAwardObs, awdWindowTypeObs, awdNomineeTypeObs).pipe(
					takeUntil(self._subrelease),
					map(_pck => {
						return {
							Schools: _pck[0],
							Award: _pck[1],
							WindowTypes: _pck[2],
							NomineeTypes: _pck[3]
						};
					})
				).subscribe(packet => {
					// Store data in component properties, then load remaining information that depended on award info.
					self.AvailableSchools = (packet.Schools || []);
					self.AwardInfo = packet.Award;
					self.AvailableWindowTypes = (packet.WindowTypes || []);
					self.AvailableNomineeTypes = (packet.NomineeTypes || []);

					// We can now load school groups, which may depend on the award program.
					const allYearsObs = self.appSvc.GetAllSchoolYears(self.AwardInfo.AwardID, self.CurrentSchoolYear);
					const allSchGpsObs = self.appSvc.GetAllSchoolGroups(self.AwardInfo.AwardID, self.CurrentSchoolYear);
					const availReportsObs = self.reportsSvc.GetAvailableReports(self.User.AuthToken, self.User.CurrentRole, self.AwardInfo.AwardID);

					combineLatest(allYearsObs, allSchGpsObs, availReportsObs).pipe(
						takeUntil(self._subrelease),
						map(_ipck => {
							return {
								Years: _ipck[0],
								SchoolGroups: _ipck[1],
								Reports: _ipck[2]
							};
						})
					).subscribe(ipacket => {
						self.AllSchoolYears = (ipacket.Years || []);
						self.AvailableReports = (ipacket.Reports || []);
						self.AvailableSchoolGroups = (ipacket.SchoolGroups || []);

						self.ViewReady = true;
					}, ierror => self.errorSvc.HandleServiceError(ierror));
				}, error => self.errorSvc.HandleServiceError(error));
			});
        }, error => self.errorSvc.HandleServiceError(error));
    }

    RunReport(report: ReportInfo) {
        const self = this;

        // This requires collecting options. However, if the current role is not a system admin or developer, then
        // the school should be narrowed down to only valid schools.
        let validSchools = <SchoolInfo[]>[];

        if (self.User.CurrentRole !== 'SystemAdmin' && self.User.CurrentRole !== 'Developer') {
            const userSchool = self.User.SchoolNumber;

            // Reduce typically down to a single school - the one this employee is from. However, if the current employee is
            // from a school group, they can crosscheck between each other.
            const schoolGroup = self.AvailableSchoolGroups.filter(item => {
                if (!item.Schools || !item.Schools.length) { return false; }

                const foundSchool = item.Schools.filter(schitem => schitem.LocationNumber === userSchool);
                if (!foundSchool || !foundSchool.length) { return false; }

                return true;
            });

            for (let sch of self.AvailableSchools) {
                let grpSch = false;

                if (schoolGroup && !!schoolGroup.length) {
                    const foundgschool = schoolGroup[0].Schools.filter(item => item.LocationNumber === sch.SchoolNumber);
                    grpSch = (foundgschool && !!foundgschool.length);
                }

                if (sch.SchoolNumber === userSchool || grpSch) {
                    // Add this school.
                    validSchools.push(sch);
                }
            }
        } else {
            // This user can use any of the available schools. However, to be concise, we should make this include/replace with
            // school groups.
            validSchools = self.AvailableSchools.filter(item => {
                if (!self.AvailableSchoolGroups || !self.AvailableSchoolGroups.length) {
                    return true;
                } // No groups, so include this school.

                // Is this school in a group?
                const foundgps = self.AvailableSchoolGroups.filter(sgitem => {
                    // Use .filter() now to look within this group's list of schools for the current school number.
                    if (sgitem.Schools && !!sgitem.Schools.filter(sch => sch.LocationNumber === item.SchoolNumber).length) {
                        // The school we're checking is in this school group. Return true so this group gets kicked out.
                        return true;
                    } else {
                        return false;
                    }
                });

                return (!foundgps || !foundgps.length); // Only include if no school group was found that includes this school.
            });

            // Now, we must add each school group to the list.
			if (self.AvailableSchoolGroups && !!self.AvailableSchoolGroups.length) {
				self.AvailableSchoolGroups.forEach(item => {
					let nums: string[] = [];
					for (let s of item.Schools) {
						nums.push(s.LocationNumber);
					}

                    validSchools.push(<SchoolInfo>{
                        SchoolNumber: item.LeadLocationNumber,
						SchoolName: item.Name,
						InSchoolGroup: true,
						SchoolNumberList: nums.join(', '),
                    });
                });
            }

            // Lastly, sort by school name
            validSchools = validSchools.sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));

            validSchools.unshift(<SchoolInfo>{
                SchoolName: 'Entire District',
                SchoolNumber: '0000'
            });
        }

        // Pop up the dialog box to collect the options for the report before sending the request to the service.
        self.dialogSvc.OpenReportOptionsDialog({
            TitleText: 'Choose Report Options',
            MessageText: `Please set the following options for the report to be generated:`,
            OKText: 'Run Report',
            CancelText: 'Cancel',
            AvailableSchools: validSchools,
            AvailableWindowTypes: self.AvailableWindowTypes.filter(t => t.JudgingPhase != null ),
            AllSchoolYears: self.AllSchoolYears,
            AvailableNomineeTypes: self.AvailableNomineeTypes.filter(t => t.AwardID === self.AwardInfo.AwardID),
            ReportInfo: report
        }).pipe(takeUntil(self._subrelease)).subscribe(options => {
            if (options != null) {
                // User has run the report. Use the options provided to make the call to the service and
                // download the report.
                self.splashSvc.OpenSplash({
                    TitleText: 'Please Wait',
                    MessageText: 'Generating Report...'
                }).pipe(takeUntil(self._subrelease)).subscribe(() => {
                    // Download the file...
                    self.downloadSvc.RetrieveDownload(HttpMethod.GET, 'Api/Services/Report/RunReport', {
                        AuthToken: self.User.AuthToken,
                        Role: self.User.CurrentRole,
                        AwardID: self.AwardInfo.AwardID,
                        NomineeTypeID: options.ReportNomineeType,
                        ReportID: options.ReportInfo.ReportID,
                        SchoolYear: options.ReportYear,
                        SchoolNumber: (options.ReportSchool != null ? options.ReportSchool.SchoolNumber : null),
                        SchoolName: (options.ReportSchool != null ? options.ReportSchool.SchoolName : null),
                        JudgingPhase: (options.ReportJudgingPhase != null ? options.ReportJudgingPhase.JudgingPhase : null),
                        Attachment: true
                    }, options.ReportInfo.ReportFileType).pipe(takeUntil(self._subrelease)).subscribe(fileresult => {
                        // Drop the splash
                        self.splashSvc.CloseSplash();
                    }, fileerror => self.errorSvc.HandleServiceError(fileerror));
                });
            }
        });
    }

}
